import { useRef, useMemo, useState, useContext, useCallback, createContext } from 'react';

import type { JobChecklist } from '../components/job-checklist';
import type { EvaluationCriteria } from './job-evaluation-context';

interface JobStepperContextType {
  activeStep: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  jobNameDraft: string;
  setJobNameDraft: React.Dispatch<React.SetStateAction<string>>;
  jobDescriptionDraft: {
    text: string;
    html: string;
  };
  setJobDescriptionDraft: React.Dispatch<
    React.SetStateAction<{
      text: string;
      html: string;
    }>
  >;
  handleStepClick: (step: number) => void;
  completed: {
    [k: number]: boolean;
  };
  setCompleted: React.Dispatch<
    React.SetStateAction<{
      [k: number]: boolean;
    }>
  >;
  isStepCompleted: (step: number) => boolean;
  checklistDraft: string[] | null;
  setChecklistDraft: React.Dispatch<React.SetStateAction<string[] | null>>;
  jobChecklistDraft: JobChecklist[] | null;
  setJobChecklistDraft: React.Dispatch<React.SetStateAction<JobChecklist[] | null>>;
  evaluationCriteriaDraft: EvaluationCriteria[] | null;
  setEvaluationCriteriaDraft: React.Dispatch<React.SetStateAction<EvaluationCriteria[] | null>>;
  registerStepDraftSaver: (fn: () => void) => void;
  jobDetailsDraft: {
    country: string;
    city: string;
    mode: string;
    type: string;
  };
  setJobDetailsDraft: React.Dispatch<React.SetStateAction<{
    country: string;
    city: string;
    mode: string;
    type: string;
  }>>;
}

const JobStepperContext = createContext<JobStepperContextType | undefined>(undefined);

export const JobStepperContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [jobNameDraft, setJobNameDraft] = useState('');
  const [jobDetailsDraft, setJobDetailsDraft] = useState({
    country: '',
    city: '',
    mode: 'Hybrid',
    type: 'Full-Time'
  });
  const [jobDescriptionDraft, setJobDescriptionDraft] = useState({
    text: '',
    html: '',
  });
  const [completed, setCompleted] = useState<{
    [k: number]: boolean;
  }>({});
  const stepDraftGetterRef = useRef<(() => void) | null>(null);
  const [evaluationCriteriaDraft, setEvaluationCriteriaDraft] = useState<EvaluationCriteria[] | null>(null);
  const [checklistDraft, setChecklistDraft] = useState<string[] | null>(null);
  const [jobChecklistDraft, setJobChecklistDraft] = useState<JobChecklist[] | null>(null);

  const isStepCompleted = useCallback((step: number) => completed[step], [completed]);

  const registerStepDraftSaver = useCallback((fn: () => void) => {
    stepDraftGetterRef.current = fn;
  }, []);

  const handleStepClick = useCallback(
    (step: number) => {
      if (!isStepCompleted(step)) return;
      if(step === activeStep) return;
  
      stepDraftGetterRef.current?.();
  
      setActiveStep(step);
    },
    [isStepCompleted, activeStep]
  )

  const value = useMemo(
    () => ({
      activeStep,
      setActiveStep,
      checklistDraft,
      setChecklistDraft,
      jobChecklistDraft,
      setJobChecklistDraft,
      evaluationCriteriaDraft,
      setEvaluationCriteriaDraft,
      jobNameDraft,
      setJobNameDraft,
      jobDescriptionDraft,
      setJobDescriptionDraft,
      completed,
      setCompleted,
      jobDetailsDraft,
      setJobDetailsDraft,
      handleStepClick,
      isStepCompleted,
      registerStepDraftSaver
    }),
    [
      activeStep,
      checklistDraft,
      jobChecklistDraft,
      evaluationCriteriaDraft,
      jobNameDraft,
      jobDescriptionDraft,
      jobDetailsDraft,
      handleStepClick,
      completed,
      isStepCompleted,
      registerStepDraftSaver
    ]
  );

  return <JobStepperContext.Provider value={value}>{children}</JobStepperContext.Provider>;
};

export const useJobStepper = () => {
  const context = useContext(JobStepperContext);
  if (!context) {
    throw new Error('useJobStepper must be used within a JobStepperContextProvider');
  }
  return context;
};
